import { useCallback, useEffect, useState } from "react";
import { getCsrfHeader } from "../lib/csrf";

export type OpenRouterKeyStatus = {
  configured: boolean;
  updatedAt: string | null;
  limitUsd?: number | null;
  usageUsd?: number | null;
};

export type OpenRouterKeyFetchStatus = "loading" | "ready" | "error";

export type OpenRouterKeyErrorCode =
  | "INVALID_KEY"
  | "OPENROUTER_UNAVAILABLE"
  | "PUSH_FAILED"
  | "WRONG_AI_MODE"
  | "FEATURE_DISABLED"
  | "FORBIDDEN"
  | "INVALID_STATE"
  | "NOT_FOUND"
  | "INVALID"
  | "UNAUTHORIZED"
  | "UNKNOWN";

export type OpenRouterKeyResult =
  | { ok: true; value: OpenRouterKeyStatus }
  | { ok: false; code: OpenRouterKeyErrorCode; message: string };

export type UseOpenRouterKeyResult = {
  status: OpenRouterKeyFetchStatus;
  keyStatus: OpenRouterKeyStatus | null;
  setKey: (key: string) => Promise<OpenRouterKeyResult>;
};

type ServerError = { code: string; message: string };

const ERROR_CODES: ReadonlyArray<OpenRouterKeyErrorCode> = [
  "INVALID_KEY", "OPENROUTER_UNAVAILABLE", "PUSH_FAILED", "WRONG_AI_MODE",
  "FEATURE_DISABLED", "FORBIDDEN", "INVALID_STATE", "NOT_FOUND", "INVALID",
  "UNAUTHORIZED", "UNKNOWN",
];

const narrowCode = (code: string): OpenRouterKeyErrorCode =>
  ERROR_CODES.includes(code as OpenRouterKeyErrorCode)
    ? (code as OpenRouterKeyErrorCode)
    : "UNKNOWN";

export const useOpenRouterKey = (tenantId: string): UseOpenRouterKeyResult => {
  const [status, setStatus] = useState<OpenRouterKeyFetchStatus>("loading");
  const [keyStatus, setKeyStatus] = useState<OpenRouterKeyStatus | null>(null);

  const url = `/api/claws/${encodeURIComponent(tenantId)}/openrouter-key`;

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const res = await fetch(url, { credentials: "include" });
        if (cancelled) return;
        if (res.status === 200) {
          const body = (await res.json()) as OpenRouterKeyStatus;
          if (cancelled) return;
          setKeyStatus(body);
          setStatus("ready");
        } else {
          setStatus("error");
        }
      } catch {
        if (!cancelled) setStatus("error");
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [url]);

  const setKey = useCallback(
    async (key: string): Promise<OpenRouterKeyResult> => {
      try {
        const res = await fetch(url, {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify({ key }),
        });
        if (res.status === 200) {
          const body = (await res.json()) as OpenRouterKeyStatus;
          setKeyStatus(body);
          setStatus("ready");
          return { ok: true, value: body };
        }
        if (res.status === 401) {
          return { ok: false, code: "UNAUTHORIZED", message: "Session expired. Refresh to sign in again." };
        }
        const body = (await res.json().catch(() => null)) as ServerError | null;
        return {
          ok: false,
          code: narrowCode(body?.code ?? "UNKNOWN"),
          message: body?.message ?? `Couldn't update the key (${res.status})`,
        };
      } catch {
        // The apply can take ~30s (SSM push + gateway restart) and the edge
        // may time the request out even though the box-side update lands.
        return {
          ok: false,
          code: "UNKNOWN",
          message:
            "We couldn't confirm the update — your instance may still be restarting. Check the credits figure in a minute before retrying.",
        };
      }
    },
    [url],
  );

  return { status, keyStatus, setKey };
};
